import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Mail, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const AIContactChat = () => {

  const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hey there! 👋 I'm Rachel's AI assistant at Manik Studio! Here's how this works: I'll chat with you about your web development, app, or tech consultation needs, then help you compose a personalized email to Rachel with all our discussion plus your contact details.\n\nReady to dive into your project ideas? What are you looking to build? ✨"
    }
  ]);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle'); // idle, sending, sent, error
  const [emailData, setEmailData] = useState({
    name: '',
    email: '',
    subject: 'Project Inquiry from Manik Studio Chat',
    message: ''
  });
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const hasAutoSentRef = useRef(false);

  // for auto-send after inactivity
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const [autoSendStatus, setAutoSendStatus] = useState('idle'); // idle, sending, sent
  // Configuration constants
  const INACTIVITY_TIMEOUT = 3 * 60 * 1000; // 3 minutes  // FOR TESTING // 10 * 1000;
  const MIN_MESSAGES_FOR_AUTO_SEND = 3; // only send if there are at least 3 messages


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // scrollToBottom();
  }, [messages]);

  // Single useEffect for timer management
  useEffect(() => {
    // Start timer after first real conversation (user message + AI response)
    if (messages.length >= 3 && !hasAutoSentRef.current && !inactivityTimer) {
      setTimeout(() => resetInactivityTimer(), 500);
    }

    // Cleanup on unmount
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  }, [messages.length]); // Only depend on length, not entire messages array


  // System prompt with business info
  const getSystemPrompt = () => `You are an AI assistant for Manik Studio, owned by Rachel Beneroff. Your job is to help potential clients articulate their needs and compose a well-structured email to Rachel.

ABOUT RACHEL & MANIK STUDIO:
- Principal Engineer with 8+ years experience
- Recently founded Manik Studio after being Principal Engineer at Joylab
- Services: Web design & development, Custom app development, Technical consultation
- Skills: Full-stack development, React, .NET, JavaScript, mobile apps, database architecture, AI integration
- Based in Los Angeles, CA
- Website: https://manik-studio.vercel.app/
- Contact: rachel@manik-studio.com

PRIVACY & CONTACT RULES:
- NEVER share Rachel's phone number or any personal contact info beyond her email
- Only provide rachel@manik-studio.com as the contact method
- If asked for phone number, explain that email is the preferred contact method
- Do not share any personal details not listed above

STRICT TOPIC BOUNDARIES:
- PRIMARY: Discuss topics related to web development, app development, technical consultation, project inquiries, business services
- FUN TOPICS ALLOWED: Random hypotheticals, "would you rather" questions, quirky scenarios (as conversation starters)
- DO NOT engage with: personal questions about Rachel beyond professional background, inappropriate content, technical support for existing projects, detailed coding help, or extended off-topic conversations
- Use fun topics as ice-breakers but always steer back to business after a few exchanges
- If someone asks off-topic questions, engage briefly with fun topics if appropriate, then redirect to project needs
- If someone is inappropriate or abusive, end the conversation and direct them to email
- Keep all conversations ultimately business-focused while being personable

CONVERSATION GOALS:
- Understand their project type (web dev, app dev, consultation)
- Learn about timeline, budget, and requirements
- Help them compose a professional inquiry email
- Stay focused on qualifying and understanding new business opportunities
- Be friendly, enthusiastic, and personable while maintaining professionalism
- Use conversational language and appropriate emojis to create a welcoming experience
- Show genuine interest in their projects and ideas`;

  // SIMULATED AI RESPONSES
  const simulateAIResponse = async (userInput) => {
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000)); // Realistic delay

    const input = userInput.toLowerCase();

    // Get conversation context for more dynamic responses
    const messageCount = messages.length;
    const hasDiscussedBudget = messages.some(msg => msg.content.toLowerCase().includes('budget') || msg.content.toLowerCase().includes('cost') || msg.content.toLowerCase().includes('$'));
    const hasDiscussedTimeline = messages.some(msg => msg.content.toLowerCase().includes('timeline') || msg.content.toLowerCase().includes('week') || msg.content.toLowerCase().includes('month'));
    const hasDiscussedProjectType = messages.some(msg => msg.content.toLowerCase().includes('website') || msg.content.toLowerCase().includes('app') || msg.content.toLowerCase().includes('development'));

    // Check for different types of input
    const funHypotheticals = ['would you rather', 'hypothetical', 'random question', 'fun question', 'what would you choose', 'desert island', 'superpower', 'zombie apocalypse', 'time travel', 'if you could', 'imagine if'];
    const isFunHypothetical = funHypotheticals.some(keyword => input.includes(keyword));

    if (isFunHypothetical || input.includes('random') || input.includes('fun')) {
      const hypotheticals = [
        "Ooh, fun question! 🤔 Would you rather have the ability to debug any code just by looking at it, OR have every website you build automatically be responsive on every device ever made? (I'd personally go with the debugging superpower - imagine never having to hunt for semicolons again! 😂) Speaking of building things though, what kind of project brings you here today? 💡",
        "Haha, I love hypotheticals! 🎭 Here's one: Would you rather live in a world where every app loads instantly but crashes after 5 minutes, OR every app takes 2 minutes to load but never crashes? (Definitely the slow-but-reliable option for me!) But seriously, are you working on any app ideas that I can help you explore? 📱✨",
        "Fun question time! 🌟 If you could only use 3 colors for every website for the rest of your life, what would they be? (I'm thinking blue, white, and... maybe a pop of orange? 🧡) Color schemes are actually super important in web design - are you thinking about a website project by any chance? 🎨",
        "Ooh I've got one! 🚀 Would you rather fight one horse-sized bug in your code, or 100 duck-sized bugs? (Definitely the horse-sized bug - at least you'd know where to look! 😄) But hey, what kind of digital project are you dreaming up these days?",
        "Here's a wild one! 🌍 If you had to choose between having telepathic communication with computers OR the ability to time travel but only to fix coding mistakes from the past, which would you pick? (Time travel sounds amazing until you realize you'd be stuck in an endless loop of 'just one more fix!' 😅) What brings you to Manik Studio today though?"
      ];
      return hypotheticals[Math.floor(Math.random() * hypotheticals.length)];
    } else if (input.includes('website') || input.includes('web')) {
      const webResponses = [
        "Ooh, a website project! 🌐 Rachel absolutely loves web development! Are you thinking of a brand new site, sprucing up an existing one, or maybe adding some cool new features? And hey, any special requirements like e-commerce, user logins, or integrations with other tools? The more details, the better! 💡",
        "Website work is awesome! 🎨 What's the vibe you're going for? Something clean and minimal, or more interactive and dynamic? Rachel's built everything from simple business sites to complex web applications. Tell me more about what you have in mind! ✨",
        "Web development time! 💻 Is this for a business, personal project, or something else entirely? Rachel loves understanding the 'why' behind a website - it helps create something that really serves your goals! What's the main purpose of this site? 🚀"
      ];
      return webResponses[Math.floor(Math.random() * webResponses.length)];
    } else if (input.includes('app')) {
      const appResponses = [
        "Apps are SO exciting! 📱✨ Rachel has built some seriously cool stuff - both mobile apps and web applications. Are you dreaming of a mobile app (iOS/Android), a web app, or maybe both? And I'm super curious - what's the main goal? Is it for running your business, something customer-facing, or a totally new idea? Tell me more! 🚀",
        "App development is Rachel's jam! 💫 What kind of problem are you trying to solve with this app? The best apps usually start with someone saying 'I wish there was an easier way to...' - what's your 'easier way' moment? 🤔",
        "Mobile or web app? Both are exciting! 📲 Rachel's experience spans everything from business tools to customer-facing apps. What's got you thinking about building an app? Is it scratching your own itch or solving something for others? 🎯"
      ];
      return appResponses[Math.floor(Math.random() * appResponses.length)];
    } else if (input.includes('budget') || input.includes('cost')) {
      if (hasDiscussedBudget) {
        return "Got it on the budget front! 💰 That helps narrow things down. What about timeline - when are you hoping to launch this project? And are there any must-have features or deal-breakers we should know about? 📅✨";
      } else {
        return "Ah yes, the important stuff! 💰 Rachel's projects typically range from $5k-$50k+ depending on how complex and awesome we're making it! Simple websites usually run $5k-$15k, custom web apps start around $15k-$30k, and mobile apps can be $20k-$50k+. What range feels comfortable for your vision? No judgment here - there's usually a way to make great things happen at different budgets! 😊";
      }
    } else if (input.includes('timeline') || input.includes('when')) {
      if (hasDiscussedTimeline) {
        return "Perfect! Sounds like we're getting a good picture of your project. 🎯 What else can I help clarify? Maybe specific features you're excited about or any integrations you'll need? 🛠️";
      } else {
        return "Timeline-wise, we're usually looking at 4-12 weeks of creative magic! ⏰ Simple websites might wrap up in 4-6 weeks, while custom applications could take 8-12 weeks or more (the cool stuff takes time, you know? 😉). When's your dream launch date? This helps figure out if we can make the timing work perfectly!";
      }
    } else if (input.includes('phone') || input.includes('call') || input.includes('number')) {
      return "Rachel's all about that email life at rachel@manik-studio.com! 📧 She finds that starting with email helps her really understand your project and give you a thoughtful response (plus, she can reference it later when the creative juices are flowing!). Once you connect there, you two can figure out the best way to keep the conversation going! 💬";
    } else if (input.includes('email') || input.includes('contact') || input.includes('ready')) {
      return "Awesome! Sounds like we've got a good picture of what you're looking for! 🎨 I can totally help you put together an email to Rachel with all the juicy details we've covered. She loves getting well-thought-out project inquiries - it gets her excited about the possibilities! Ready to craft that email? I'll just need your name and email to get started! ✉️✨";
    } else if (input.includes('hello') || input.includes('hi') || input.includes('hey')) {
      const greetingResponses = [
        "Hey there! 👋 Great to meet you! What kind of digital project is on your mind today? 🚀",
        "Hi! 😊 Welcome to Manik Studio! Are you thinking about a website, app, or maybe some technical consultation? I'd love to hear about it! ✨",
        "Hello! 🌟 So nice to have you here! What brings you to chat with us today? Got a cool project brewing? 💡"
      ];
      return greetingResponses[Math.floor(Math.random() * greetingResponses.length)];
    } else if (!hasDiscussedProjectType && messageCount > 5) {
      return "I'm totally focused on helping with project inquiries for Manik Studio! 🎯 Whether it's web development, app development, or technical consultation - that's my jam! So tell me, what kind of digital magic are you looking to create? ✨";
    } else {
      // Context-aware default responses
      if (!hasDiscussedProjectType) {
        return "That's interesting! 🤔 To help me understand how Rachel might be able to help, what type of project are you thinking about? Website, mobile app, web application, or maybe some technical consultation? 💭";
      } else if (!hasDiscussedBudget && !hasDiscussedTimeline) {
        return "That's super helpful context! 🤗 I'd love to know more about your timeline and budget ballpark - it really helps Rachel understand if she's the perfect fit for bringing your vision to life! Also, any specific features or technical requirements floating around in your head? Even if they seem crazy, let's talk about them! Sometimes the 'crazy' ideas turn into the coolest features! 🚀💡";
      } else {
        return "Awesome details! 🎉 This is really helping me understand your project. What other aspects should we cover? Maybe specific features you're excited about, or any challenges you're trying to solve? The more I know, the better I can help you craft that perfect email to Rachel! 📝✨";
      }
    }
  };

  // Claude API call
  const callClaudeAPI = async (userInput) => {
    const conversationHistory = messages.map(msg => ({
      role: msg.type === 'ai' ? 'assistant' : 'user',
      content: msg.content
    }));

    conversationHistory.push({
      role: 'user',
      content: userInput
    });

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: conversationHistory,
          systemPrompt: getSystemPrompt()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.content;
    } catch (error) {
      console.error('Error calling Claude API:', error);
      throw error;
    }
  };

  // AI Response Handler
  const getAIResponse = async (userInput) => {
    if (DEMO_MODE) {
      // SIMULATION MODE
      return await simulateAIResponse(userInput);
    } else {
      // API MODE
      return await callClaudeAPI(userInput);
    }
  };

  const autoSendConversation = async () => {
    // Use ref for immediate check to prevent race conditions
    if (hasAutoSentRef.current || hasAutoSent || showEmailPreview) {
      return;
    }

    // Get fresh messages from state by using a function callback
    setMessages(currentMessages => {
      if (currentMessages.length < MIN_MESSAGES_FOR_AUTO_SEND) {
        return currentMessages; // Return unchanged
      }

      // Set ref immediately to prevent other timers from proceeding
      hasAutoSentRef.current = true;

      // Use the fresh currentMessages here
      const conversationTranscript = currentMessages
        .map(msg => `${msg.type === 'ai' ? 'AI Assistant' : 'Website Visitor'}: ${msg.content}`)
        .join('\n\n');

      const autoEmailBody = `A visitor had a conversation with your AI assistant but left without manually sending an email. 

  Here's their conversation transcript:`;

      // Set status after we've captured the messages
      setHasAutoSent(true);
      setAutoSendStatus('sending');

      // Send the email
      const sendEmail = async () => {
        if (DEMO_MODE) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log('🔄 AUTO-SEND DEMO: Conversation would be auto-sent with this data:', {
            to: 'rachel@manik-studio.com',
            from: 'auto-chat@manik-studio.com',
            subject: 'Auto-forwarded Chat - Potential Lead',
            message: autoEmailBody,
            conversationTranscript
          });
          setAutoSendStatus('sent');
        } else {
          try {
            const response = await fetch('/api/send-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                to: 'rachel@manik-studio.com',
                from: 'auto-chat@manik-studio.com',
                fromName: 'Manik Studio Auto-Chat',
                subject: 'Auto-forwarded Chat Conversation - Potential Lead',
                message: autoEmailBody,
                conversationTranscript
              }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            setAutoSendStatus('sent');
          } catch (error) {
            console.error('Error auto-sending conversation:', error);
            setAutoSendStatus('idle');
            setHasAutoSent(false);
            hasAutoSentRef.current = false;
          }
        }
      };

      // Call the send function
      sendEmail();

      return currentMessages; // Return unchanged messages
    });
  };

  const resetInactivityTimer = () => {

    // Clear any existing timer
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // Don't set new timer if already sent or not enough messages
    if (hasAutoSentRef.current || hasAutoSent || showEmailPreview || messages.length < MIN_MESSAGES_FOR_AUTO_SEND) {
      setInactivityTimer(null);
      return;
    }

    // Set new timer
    const newTimer = setTimeout(() => {
      autoSendConversation();
    }, INACTIVITY_TIMEOUT);

    setInactivityTimer(newTimer);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const aiResponse = await getAIResponse(userMessage.content);

      const newMessages = [...messages, userMessage, {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse
      }];

      setMessages(newMessages);

      // Reset timer after both messages are added
      setTimeout(() => resetInactivityTimer(), 300);

    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: DEMO_MODE
          ? "Oops! Even my simulation had a hiccup! 😅 Try asking me something else - maybe about your project ideas? I'm much better at that!"
          : "I'm having trouble connecting right now. Please try again in a moment, or feel free to contact Rachel directly at rachel@manik-studio.com. Thanks for your patience! 😊"
      }]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleComposeEmail = () => {
    // Clear inactivity timer since user is actively engaging
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      setInactivityTimer(null);
    }

    // Generate conversation summary for email
    const conversationSummary = messages
        .filter(msg => msg.type === 'user')
        .map(msg => msg.content)
        .join(' ');

    let autoMessage = '';

    if (messages.length <= 1) {
        autoMessage = `Hi Rachel,

    I'm interested in learning more about your services at Manik Studio.

    [Please describe your project needs, timeline, and any specific requirements here]

    Looking forward to hearing from you!`;
    } else {
        autoMessage = `Hi Rachel,

    I had a chat with your AI assistant about my project needs. Here's a quick summary of what we discussed:

    `;

        if (conversationSummary.toLowerCase().includes('website') || conversationSummary.toLowerCase().includes('web')) {
        autoMessage += `• I'm interested in web development services\n`;
        }
        if (conversationSummary.toLowerCase().includes('app')) {
        autoMessage += `• I'm looking into app development\n`;
        }
        if (conversationSummary.toLowerCase().includes('budget') || conversationSummary.toLowerCase().includes('cost')) {
        autoMessage += `• We discussed budget considerations\n`;
        }
        if (conversationSummary.toLowerCase().includes('timeline') || conversationSummary.toLowerCase().includes('when')) {
        autoMessage += `• We talked about project timeline\n`;
        }

        autoMessage += `
    Please find our full conversation below for context. I'd love to discuss this project further with you!

    Looking forward to hearing from you.

    Best regards,`;
    }

    setEmailData(prev => ({
        ...prev,
        message: autoMessage
    }));
    setShowEmailPreview(true);
  };

  const sendEmail = async () => {
    if (!emailData.name || !emailData.email || !emailData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    setEmailStatus('sending');

    try {
      // Generate conversation transcript
      const conversationTranscript = messages
        .map(msg => `${msg.type === 'ai' ? 'AI Assistant' : emailData.name || 'Visitor'}: ${msg.content}`)
        .join('\n\n');

      const fullEmailBody = `${emailData.message}`;

      if (DEMO_MODE) {
        // 🧪 SIMULATE EMAIL SENDING
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API delay
        console.log('📧 DEMO: Email would be sent with this data:', {
          to: 'rachel@manik-studio.com',
          from: emailData.email,
          fromName: emailData.name,
          subject: emailData.subject,
          message: fullEmailBody,
          conversationTranscript
        });
        setEmailStatus('sent');
      } else {
        // REAL EMAIL SENDING
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: 'rachel@manik-studio.com',
            from: emailData.email,
            fromName: emailData.name,
            subject: emailData.subject,
            message: fullEmailBody,
            conversationTranscript
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setEmailStatus('sent');
      }

      setTimeout(() => {
        setShowEmailPreview(false);
        setEmailStatus('idle');
        // Reset form
        setEmailData({
          name: '',
          email: '',
          subject: 'Project Inquiry from Manik Studio Chat',
          message: ''
        });
      }, 2000);

    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus('error');
    }
  };

  if (showEmailPreview) {
    return (
      <div>
        <div className="email-container max-w-2xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="p-6 pb-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Email Manik Studio</h2>
            </div>
          </div>

        <div className="p-6 space-y-4">
          {emailStatus === 'sent' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800">
                {DEMO_MODE
                  ? "Demo: Email simulation complete! Check the console to see what would be sent."
                  : "Email sent successfully! Rachel will get back to you soon."
                }
              </span>
            </div>
          )}

          {emailStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">Failed to send email. Please try again or contact Rachel directly.</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">Your Name *</label>
            <input
              type="text"
              value={emailData.name}
              onChange={(e) => setEmailData({...emailData, name: e.target.value})}
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
              placeholder="Your full name"
              disabled={emailStatus === 'sending'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">Your Email *</label>
            <input
              type="email"
              value={emailData.email}
              onChange={(e) => setEmailData({...emailData, email: e.target.value})}
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
              placeholder="your@email.com"
              disabled={emailStatus === 'sending'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">Subject</label>
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
              disabled={emailStatus === 'sending'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-900 mb-1">Message</label>
            <textarea
              value={emailData.message}
              onChange={(e) => setEmailData({...emailData, message: e.target.value})}
              rows="10"
              className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900"
              disabled={emailStatus === 'sending'}
            />
          </div>

          <div className="flex justify-between gap-3 pt-4">
            <button
              onClick={sendEmail}
              disabled={emailStatus === 'sending' || !emailData.name || !emailData.email}
              className="send-email btn-primary-blue duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emailStatus === 'sending' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Email
                </>
              )}
            </button>
            <button
              onClick={() => setShowEmailPreview(false)}
              disabled={emailStatus === 'sending'}
              className="px-4 py-2 btn-secondary transition-colors disabled:opacity-50"
            >
              Back to Chat
            </button>
          </div>
        </div>
      </div>
      <div className="note max-w-2xl mx-auto rounded-lg shadow-lg overflow-hidden mb-8 text-xs text-blue-500 bg-blue-50 p-3 rounded-md">
          <strong>Note:</strong> Your complete conversation transcript will be automatically included with this email to provide Rachel with full context.
        </div>
      </div>
    );
  }

  return (
    <div className="chat-box mx-auto overflow-hidden">
      <div className="blur-effect"></div>
      {/* Header */}
      {/* <div className="p-6 pt-0 pb-2">
        <div className="flex items-center flex-row flex-wrap gap-3">
            <h2 className="">Chat with Manik Studio</h2>
        </div>
      </div> */}

      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="h-96 overflow-y-auto p-6 space-y-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`bot-blurb flex gap-3 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.type === 'ai' ? 'text-white' : 'text-white'}`}>
                {message.type === 'ai' ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </div>
              <div className={`rounded-lg pb-3 ${
                message.type === 'ai'
                  ? ' shadow-sm'
                  : ' shadow-sm'
              }`}>
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 justify-start">
            <div className="flex gap-3 max-w-[80%]">
              <div className="w-8 h-8 rounded-full text-white flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4" />
              </div>
              <div className="text-white shadow-sm rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Auto-send notification */}
      {autoSendStatus === 'sent' && (
        <div className="px-6 pb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-2 text-blue-700">
              <Mail className="w-4 h-4" />
              <span>
                {DEMO_MODE
                  ? "Demo: This conversation would be auto-forwarded to Rachel"
                  : "This conversation has been automatically forwarded to Rachel"
                }
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-3">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Describe your project or ask a question..."
            className="flex-1 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900 text-sm"
            rows="2"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="send-message px-4 py-2 bg-blue-900 text-white rounded-md hover:from-blue-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-3 flex gap-2 flex-wrap items-center">
          <button
            onClick={handleComposeEmail}
            // disabled={messages.length < 3} // disabled:opacity-50 disabled:cursor-not-allowed
            className="text-sm btn-primary text-white flex items-center gap-1"
          >
            <Mail className="w-3 h-3" />
            Compose Email
          </button>
          <span className="text-sm text-white">
           <span className="hide-on-mobile"> • </span>{messages.length > 1 ? 'Conversation transcript will be included automatically' : 'Skip the chat and contact Rachel directly'}
            </span>
            {DEMO_MODE && <span className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded text-xs font-medium">dev mode</span>}
        </div>
      </div>
    </div>
  );
};

export default AIContactChat;